import { useCallback, useMemo } from 'react'
import { match } from 'ts-pattern'
import type { GenerationModelVersion } from 'src/services/apollo/generated/graphql'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import { useModelVersion } from 'src/stores/model'
import {
  getModelDefaultSizeDivisor,
  isDitV1Model,
  isMMDIT26AModel,
  parseModelTypeForSizeSelection,
} from 'src/utils/model'
import { useTaskSubmitFormContext } from '../../../BaseTaskSubmitForm'
import {
  ASPECT_RATIOS,
  type AspectRatio,
  calculateSizeFromRatioAndTier,
  EXTRA_ASPECT_RATIOS_MMDIT26A,
  flipAspectRatio,
  PRESET_SIZES,
  PRESET_SIZES_DITV1,
  PRESET_SIZES_DITV1_FOR_MEMBER,
  PRESET_SIZES_SDV1,
  PRESET_SIZES_SDV1_FOR_MEMBER,
  PRESET_SIZES_SDXL,
  PRESET_SIZES_SDXL_FOR_MEMBER,
  PRESET_SIZES_MMDIT26A,
  PRESET_SIZES_MMDIT26A_FOR_MEMBER,
  SIDEBAR_SIZES,
  SIZE_TIER_CONFIG,
  type SizeInfo,
  type SizeTier,
} from '../../../constants'

const SIZE_FALLBACK = 256

type SizeRange = [number, number]

const SDV1_RANGE_FOR_MEMBER = [768, 1400] as SizeRange
const SDXL_RANGE_FOR_MEMBER = [768, 1400] as SizeRange
const SDV1_RANGE = [768, 1280] as SizeRange
const SDXL_RANGE = [768, 1280] as SizeRange
const DITV1_RANGE = SDXL_RANGE
const DITV1_RANGE_FOR_MEMBER = [768, 1400] as SizeRange
// Tsubaki.2 (MMDIT26A): manual input is capped at 1400 (same as other XL models).
// 1536 sizes are only reachable through preset selection (memberPresets).
const MMDIT26A_RANGE = [768, 1280] as SizeRange
const MMDIT26A_RANGE_FOR_MEMBER = [768, 1400] as SizeRange
function sizeInRange(
  size: { width: number; height: number },
  range: SizeRange,
) {
  const { width, height } = size
  return (
    width >= range[0] &&
    width <= range[1] &&
    height >= range[0] &&
    height <= range[1]
  )
}

/**
 * Should reset size to default if returns false
 * @param args
 * @returns
 */
export function validateSize(args: {
  width?: number
  height?: number
  modelType: GenerationModelType
  isMember?: boolean
  additionalSizePresets?: SizeInfo[]
}) {
  const {
    width = SIZE_FALLBACK,
    height = SIZE_FALLBACK,
    modelType,
    isMember,
  } = args

  const { range, presets, memberRange, memberPresets } = match(modelType)
    .when(isMMDIT26AModel, () => ({
      range: MMDIT26A_RANGE,
      memberRange: MMDIT26A_RANGE_FOR_MEMBER, // manual input: max 1400
      presets: [...PRESET_SIZES_MMDIT26A],
      memberPresets: [
        // preset: includes 1536 sizes
        ...PRESET_SIZES_MMDIT26A,
        ...PRESET_SIZES_MMDIT26A_FOR_MEMBER,
      ],
    }))
    .with(GenerationModelType.SdxlModel, () => ({
      range: SDXL_RANGE,
      memberRange: SDXL_RANGE_FOR_MEMBER,
      presets: [...PRESET_SIZES_SDXL],
      memberPresets: [...PRESET_SIZES_SDXL, ...PRESET_SIZES_SDXL_FOR_MEMBER],
    }))
    .when(isDitV1Model, () => ({
      range: DITV1_RANGE,
      memberRange: DITV1_RANGE_FOR_MEMBER,
      presets: [...PRESET_SIZES_DITV1],
      memberPresets: [...PRESET_SIZES_DITV1, ...PRESET_SIZES_DITV1_FOR_MEMBER],
    }))
    .otherwise(() => ({
      range: SDV1_RANGE,
      memberRange: SDV1_RANGE_FOR_MEMBER,
      presets: [...PRESET_SIZES_SDV1],
      memberPresets: [...PRESET_SIZES_SDV1, ...PRESET_SIZES_SDV1_FOR_MEMBER],
    }))

  // Tier-calculated sizes must also pass validation as valid presets
  const allRatios = isMMDIT26AModel(modelType)
    ? [...ASPECT_RATIOS, ...EXTRA_ASPECT_RATIOS_MMDIT26A]
    : ASPECT_RATIOS
  const tierSizeConfig =
    SIZE_TIER_CONFIG[modelType as keyof typeof SIZE_TIER_CONFIG] ??
    SIZE_TIER_CONFIG[GenerationModelType.SdxlModel]
  for (const ratio of allRatios) {
    for (const tier of ['medium', 'large'] as SizeTier[]) {
      if (ratio.availableAt && !ratio.availableAt.includes(tier)) continue
      const s = calculateSizeFromRatioAndTier(ratio, tier, modelType)
      const flipped: [number, number] = [s.height, s.width]
      const entry: [number, number] = [s.width, s.height]
      if (!tierSizeConfig[tier].memberOnly) {
        presets.push(entry, flipped)
      }
      memberPresets.push(entry, flipped)
    }
  }

  if (args.additionalSizePresets) {
    for (const customPreset of args.additionalSizePresets) {
      if (!customPreset.extraProps?.memberOnly) {
        presets.push([customPreset.width, customPreset.height])
      }
      memberPresets.push([customPreset.width, customPreset.height])
    }
  }

  const isInPreset = presets.some(it => it[0] === width && it[1] === height)
  const isInMemberPreset = memberPresets.some(
    it => it[0] === width && it[1] === height,
  )
  const isInRange = sizeInRange({ width, height }, range)
  const isInMemberRange = sizeInRange({ width, height }, memberRange)

  const alwaysAllowed = isInPreset || isInRange
  const memberAllowed = isInMemberPreset || isInMemberRange
  return {
    allowed: isMember ? memberAllowed : alwaysAllowed,
    memberOnly: memberAllowed && !alwaysAllowed,
  }
}

/**
 * Get the broadest valid size bounds for manual input.
 * Merges the member manual-input range with preset sizes so that
 * preset dimensions (e.g. SDV1's 512-edge sizes) are never clamped.
 */
export function getModelSizeBounds(modelType: GenerationModelType): {
  min: number
  max: number
} {
  const [memberRange, presets] = match(modelType)
    .when(
      isMMDIT26AModel,
      () =>
        [
          MMDIT26A_RANGE_FOR_MEMBER,
          [...PRESET_SIZES_MMDIT26A, ...PRESET_SIZES_MMDIT26A_FOR_MEMBER],
        ] as const,
    )
    .when(
      isDitV1Model,
      () =>
        [
          DITV1_RANGE_FOR_MEMBER,
          [...PRESET_SIZES_DITV1, ...PRESET_SIZES_DITV1_FOR_MEMBER],
        ] as const,
    )
    .with(
      GenerationModelType.SdxlModel,
      () =>
        [
          SDXL_RANGE_FOR_MEMBER,
          [...PRESET_SIZES_SDXL, ...PRESET_SIZES_SDXL_FOR_MEMBER],
        ] as const,
    )
    .otherwise(
      () =>
        [
          SDV1_RANGE_FOR_MEMBER,
          [...PRESET_SIZES_SDV1, ...PRESET_SIZES_SDV1_FOR_MEMBER],
        ] as const,
    )
  const presetEdges = presets.flat()
  return {
    min: Math.min(memberRange[0], ...presetEdges),
    max: Math.max(memberRange[1], ...presetEdges),
  }
}

/**
 * Get multiplier (diviser) value for a given model type
 * Means the size should be divisible by it
 *
 * Will be overridden if versionExtra has valid `step` value
 */
export function getModelSizeDivisor(args: {
  modelType: GenerationModelType
  versionExtra?: GenerationModelVersion['extra']
}) {
  const { modelType, versionExtra } = args
  // `step` actually means multiplier
  const widthStepFromVersion = versionExtra?.restrictions?.width?.step
  const heightStepFromVersion = versionExtra?.restrictions?.height?.step
  const modelDefaultStep = getModelDefaultSizeDivisor(modelType)
  return {
    width: widthStepFromVersion || modelDefaultStep,
    height: heightStepFromVersion || modelDefaultStep,
  }
}

export function useImageControlSizeContext() {
  const {
    values: { modelId },
  } = useTaskSubmitFormContext()
  const modelType = useModelVersion(modelId)?.modelType
  const normalizedModelType = parseModelTypeForSizeSelection(modelType)

  const sizePredicate = useCallback(
    (sz: SizeInfo) => sz.extraProps?.modelType === normalizedModelType,
    [normalizedModelType],
  )
  return useMemo(
    () => ({
      availablePresetSizes: PRESET_SIZES.filter(sizePredicate),
      availableSidebarSizes: SIDEBAR_SIZES.filter(sizePredicate),
      presetSizes: Array.from(
        new Set([...PRESET_SIZES, ...SIDEBAR_SIZES]),
      ).filter(sizePredicate),
    }),
    [sizePredicate],
  )
}

/**
 * Find the closest matching aspect ratio for a given width and height.
 * Considers both portrait and landscape orientations.
 *
 * @param extraRatios - Additional ratios to consider.
 *   V1: these are supplements to ASPECT_RATIOS (e.g. MMDIT26A extras), merged with defaults.
 *   V2 (API-driven): pass the full API ratio set and set `replaceDefault` to avoid
 *   mixing in hardcoded V1 ratios that may not exist in the API config.
 * @param replaceDefault - When true, `extraRatios` replaces ASPECT_RATIOS entirely
 *   instead of being merged. Used by V2 API-driven mode.
 */
export function findClosestAspectRatio(
  width: number,
  height: number,
  extraRatios?: AspectRatio[],
  replaceDefault?: boolean,
): AspectRatio {
  const currentRatio = width / height

  let closestRatio = ASPECT_RATIOS[0]
  let minDiff = Infinity

  const allRatios =
    replaceDefault && extraRatios
      ? extraRatios
      : extraRatios
        ? [...ASPECT_RATIOS, ...extraRatios]
        : ASPECT_RATIOS

  for (const baseRatio of allRatios) {
    // Check the base ratio (portrait)
    const portraitRatio = baseRatio.width / baseRatio.height
    const portraitDiff = Math.abs(currentRatio - portraitRatio)
    if (portraitDiff < minDiff) {
      minDiff = portraitDiff
      closestRatio = baseRatio
    }

    // Check the flipped ratio (landscape) for non-square ratios
    if (baseRatio.width !== baseRatio.height) {
      const flippedRatio = flipAspectRatio(baseRatio)
      const landscapeRatio = flippedRatio.width / flippedRatio.height
      const landscapeDiff = Math.abs(currentRatio - landscapeRatio)
      if (landscapeDiff < minDiff) {
        minDiff = landscapeDiff
        closestRatio = flippedRatio
      }
    }
  }

  return closestRatio
}

/**
 * Find the closest matching size tier for a given width and height
 */
export function findClosestSizeTier(
  width: number,
  height: number,
  modelType: GenerationModelType,
): SizeTier {
  const longestEdge = Math.max(width, height)
  const config =
    SIZE_TIER_CONFIG[modelType as keyof typeof SIZE_TIER_CONFIG] ??
    SIZE_TIER_CONFIG[GenerationModelType.SdxlModel]

  // Find the closest tier by longest edge
  const tiers: SizeTier[] = ['medium', 'large']
  let closestTier: SizeTier = 'medium'
  let minDiff = Infinity

  for (const tier of tiers) {
    const diff = Math.abs(longestEdge - config[tier].longestEdge)
    if (diff < minDiff) {
      minDiff = diff
      closestTier = tier
    }
  }

  return closestTier
}
